/**
 * PlanBadge — small indicator shown on tasks that belong to a Smart Plan.
 *
 * Queries the `plan` table for the DbPlanInstance linked to the task's planId,
 * then shows the plan name, progress, and anchor info.
 *
 * Clicking the badge opens the Plan Timeline sheet.
 */

import { memo, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import { db } from "@/db/schema"
import { useLiveQueryImmediate } from "@/db/hooks"
import type { DbPlanInstance, DbPack } from "@totodo/operations"
import { PlanInstanceStatus, ObjectStatus } from "@totodo/operations"
import type { DbObject } from "@/db/schema"
import { getPlanTasks } from "@/db/operations"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import AutomationIcon from "@/shared/icons/pikaicons/stroke/development/automation.svg?react"
import { PlanTimeline } from "./PlanTimeline"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlanBadgeProps {
  task: DbObject
  className?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PlanBadge = memo(function PlanBadge({
  task,
  className,
}: PlanBadgeProps) {
  const { t } = useTranslation()
  const [timelineOpen, setTimelineOpen] = useState(false)

  // Only render if the task belongs to a plan
  const planId = task.planId
  const isAnchor = !!task.anchorTaskId

  // Fetch the plan instance (if it exists)
  const [planInstance] = useLiveQueryImmediate(
    () => {
      if (!planId) return undefined
      return db.plan.get(planId) as Promise<DbPlanInstance | undefined>
    },
    ["plan-badge-instance", planId],
    undefined,
  )

  // Fetch the pack for display name
  const [pack] = useLiveQueryImmediate(
    () => {
      if (!planInstance?.packId) return undefined
      return db.plan.get(planInstance.packId) as Promise<DbPack | undefined>
    },
    ["plan-badge-pack", planInstance?.packId],
    undefined,
  )

  // Count completed steps (live from actual task statuses)
  const resolvedAnchorTaskId = task.anchorTaskId ?? task.id!

  // Fetch anchor task title (for display name fallback)
  const [anchorTask] = useLiveQueryImmediate(
    () => {
      if (!task.anchorTaskId) return undefined
      return db.totodo.get(task.anchorTaskId) as Promise<DbObject | undefined>
    },
    ["plan-badge-anchor", task.anchorTaskId],
    undefined,
  )

  const [planTasks] = useLiveQueryImmediate(
    () => {
      if (!planId) return Promise.resolve([])
      return getPlanTasks(resolvedAnchorTaskId)
    },
    ["plan-badge-tasks", resolvedAnchorTaskId, planId],
    [] as DbObject[],
  )

  const progress = useMemo(() => {
    const total = planTasks.length
    const completed = planTasks.filter(
      (t) => t.status === ObjectStatus.Completed,
    ).length
    const percent = total ? Math.round((completed / total) * 100) : 0
    return { total, completed, percent }
  }, [planTasks])

  if (!planId && !isAnchor) return null

  const statusLabel =
    planInstance?.status === PlanInstanceStatus.Active
      ? t("Active")
      : planInstance?.status === PlanInstanceStatus.Completed
        ? t("Completed")
        : planInstance?.status === PlanInstanceStatus.Cancelled
          ? t("Cancelled")
          : planInstance?.status === PlanInstanceStatus.Paused
            ? t("Paused")
            : null

  // Display name: pack name → anchor task title → "Smart Plan"
  const anchorTitle = task.anchorTaskId ? anchorTask?.title : task.title
  const displayName = pack?.name || anchorTitle || t("Smart Plan")
  const planName = displayName

  // Resolve the anchor task ID for the timeline

  return (
    <>
      <button
        type="button"
        onClick={() => setTimelineOpen(true)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5",
          "bg-primary/10 text-primary",
          "text-xs font-medium",
          "hover:bg-primary/20 transition-colors cursor-pointer",
          className,
        )}
        title={statusLabel ? `${planName} — ${statusLabel}` : planName}
      >
        {progress.completed > 0 ? (
          <span style={{ zoom: 0.75 }}>
            <DynamicIcon
              type="project"
              icon={pack?.icon}
              percentage={progress.percent}
              unfinished={progress.total - progress.completed}
              status={planInstance?.status === PlanInstanceStatus.Completed ? ObjectStatus.Completed : ObjectStatus.Unfinished}
              size="xs"
              animate={false}
            />
          </span>
        ) : (
          <AutomationIcon className="size-3.5" />
        )}
        <span className="max-w-[160px] truncate">{displayName}</span>
      </button>

      {planId && (
        <PlanTimeline
          open={timelineOpen}
          onOpenChange={setTimelineOpen}
          anchorTaskId={resolvedAnchorTaskId}
          planId={planId}
        />
      )}
    </>
  )
})
